// src/components/chat/ChatSettingsModal.jsx
// Modal de configuración del chat:
//  - Grupo: renombrar, editar descripción, listar/añadir/quitar miembros, salir, eliminar.
//  - Directo: info del otro participante + opción de eliminar conversación.
// Permisos:
//  - Editar nombre/descripción/miembros: admin del grupo o admin global.
//  - Eliminar chat: creador (`createdBy`) o admin global.
//  - Salir: cualquier participante.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Spinner, Badge, ListGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import {
    updateChat, addMember, removeMember, leaveChat,
} from '../../services/chatApi';

export default function ChatSettingsModal({ show, chat, onClose, onChatChanged, onChatRemoved }) {
    const { user } = useContext(AuthContext);
    const meId = user?.id;

    const isGroup       = chat?.type === 'group';
    const isGlobalAdmin = user?.role === 'admin';
    const isGroupAdmin  = isGroup && (chat?.admins || []).some(a => String(a._id || a) === String(meId));
    const canEdit       = isGroup && (isGroupAdmin || isGlobalAdmin);

    const [name, setName]       = useState('');
    const [description, setDescription] = useState('');
    const [savingMeta, setSavingMeta]   = useState(false);

    const [candidates, setCandidates]   = useState([]);
    const [loadingCands, setLoadingCands] = useState(false);
    const [filter, setFilter]           = useState('');

    useEffect(() => {
        if (!show || !chat) return;
        setName(chat.name || '');
        setDescription(chat.description || '');
        setFilter('');
    }, [show, chat]);

    // Cargar candidatos solo cuando se puede añadir miembros.
    useEffect(() => {
        if (!show || !canEdit) return;
        setLoadingCands(true);
        api.get('/chats/candidates')
            .then(({ data }) => setCandidates(data || []))
            .catch(() => setCandidates([]))
            .finally(() => setLoadingCands(false));
    }, [show, canEdit]);

    const participantIds = useMemo(
        () => new Set((chat?.participants || []).map(p => String(p._id))),
        [chat?.participants],
    );
    const addable = useMemo(() => {
        const q = filter.trim().toLowerCase();
        return (candidates || [])
            .filter(c => !participantIds.has(String(c._id)))
            .filter(c => !q
                || (c.name || '').toLowerCase().includes(q)
                || (c.email || '').toLowerCase().includes(q));
    }, [candidates, participantIds, filter]);

    if (!chat) return null;

    const handleSaveMeta = async () => {
        if (!canEdit) return;
        const trimmed = name.trim();
        if (!trimmed) { toast.warn('El nombre no puede quedar vacío'); return; }
        setSavingMeta(true);
        try {
            const updated = await updateChat(chat._id, { name: trimmed, description });
            onChatChanged?.(updated);
            toast.success('Grupo actualizado');
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo actualizar');
        } finally { setSavingMeta(false); }
    };

    const handleAdd = async (userId) => {
        try {
            const updated = await addMember(chat._id, userId);
            onChatChanged?.(updated);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo añadir');
        }
    };

    const handleRemove = async (userId) => {
        if (!window.confirm('¿Quitar a este miembro del grupo?')) return;
        try {
            const updated = await removeMember(chat._id, userId);
            onChatChanged?.(updated);
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo quitar');
        }
    };

    const handleLeave = async () => {
        if (!window.confirm('¿Seguro que quieres salir de este grupo? Tu historial de mensajes se conserva en la conversación.')) return;
        try {
            await leaveChat(chat._id);
            onChatRemoved?.(chat._id);
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo salir');
        }
    };

    const otherDirect = !isGroup
        ? (chat.participants || []).find(p => String(p._id) !== String(meId))
        : null;

    return (
        <Modal show={show} onHide={onClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{isGroup ? 'Configuración del grupo' : 'Información del chat'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isGroup ? (
                    <>
                        <Form.Group className="mb-2">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Nombre</Form.Label>
                            <Form.Control
                                value={name}
                                onChange={e => setName(e.target.value)}
                                disabled={!canEdit}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>Descripción</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                disabled={!canEdit}
                                style={{ borderRadius: 8 }}
                            />
                        </Form.Group>
                        {canEdit && (
                            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                                <Button size="sm" variant="success" onClick={handleSaveMeta} disabled={savingMeta}>
                                    {savingMeta ? 'Guardando…' : 'Guardar cambios'}
                                </Button>
                            </div>
                        )}

                        <h6 style={{ fontWeight: 700, marginBottom: 6 }}>Participantes ({chat.participants?.length || 0})</h6>
                        <ListGroup style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 16 }}>
                            {(chat.participants || []).map(p => {
                                const adminBadge = (chat.admins || []).some(a => String(a._id || a) === String(p._id));
                                const isMe = String(p._id) === String(meId);
                                const canKick = canEdit && !isMe && String(p._id) !== String(chat.createdBy?._id || chat.createdBy);
                                return (
                                    <ListGroup.Item key={p._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px' }}>
                                        <div>
                                            <span style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</span>
                                            {isMe && <Badge bg="info" style={{ marginLeft: 6, fontSize: 10 }}>Tú</Badge>}
                                            {adminBadge && <Badge bg="primary" style={{ marginLeft: 6, fontSize: 10 }}>Admin</Badge>}
                                            <div style={{ fontSize: 11, color: 'var(--c-text-4)' }}>{p.role}</div>
                                        </div>
                                        {canKick && (
                                            <Button size="sm" variant="outline-danger" onClick={() => handleRemove(p._id)}>Quitar</Button>
                                        )}
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>

                        {canEdit && (
                            <>
                                <h6 style={{ fontWeight: 700, marginBottom: 6 }}>Añadir miembros</h6>
                                <Form.Control
                                    placeholder="Buscar persona…"
                                    value={filter}
                                    onChange={e => setFilter(e.target.value)}
                                    size="sm"
                                    className="mb-2"
                                    style={{ borderRadius: 8 }}
                                />
                                <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--c-border)', borderRadius: 8 }}>
                                    {loadingCands ? (
                                        <div style={{ textAlign: 'center', padding: 16 }}><Spinner size="sm" animation="border" /></div>
                                    ) : addable.length === 0 ? (
                                        <p style={{ color: 'var(--c-text-4)', padding: 12, fontSize: 12, margin: 0 }}>
                                            {filter.trim() ? 'Sin coincidencias.' : 'No hay candidatos disponibles.'}
                                        </p>
                                    ) : addable.map(c => (
                                        <div key={c._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--c-border)' }}>
                                            <div>
                                                <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                                                <div style={{ fontSize: 11, color: 'var(--c-text-4)' }}>{c.email || c.role}</div>
                                            </div>
                                            <Button size="sm" variant="outline-primary" onClick={() => handleAdd(c._id)}>Añadir</Button>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <h6 style={{ fontWeight: 700, marginBottom: 6 }}>Conversación con</h6>
                        <div style={{ padding: '8px 12px', border: '1px solid var(--c-border)', borderRadius: 8 }}>
                            <div style={{ fontWeight: 600 }}>{otherDirect?.name || '—'}</div>
                            <div style={{ fontSize: 12, color: 'var(--c-text-4)' }}>
                                {otherDirect?.role}{otherDirect?.email ? ` · ${otherDirect.email}` : ''}
                            </div>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                    {isGroup && (
                        <Button variant="outline-warning" size="sm" onClick={handleLeave}>Salir del grupo</Button>
                    )}
                </div>
                <Button variant="secondary" onClick={onClose}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    );
}
